import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Infinity, AlertTriangle, List, CheckSquare, Keyboard, ChevronDown, ChevronUp, Settings } from 'lucide-react';
import { Button } from './ui/Button';
import { Card } from './ui/Card';
import { GameSettings, GameMode, InputType, ProblemType, AdvancedOptions, DEFAULT_ADVANCED_OPTIONS } from '../types';
interface ModeSelectorProps {
  onStart: (settings: Omit<GameSettings, 'problemTypes'>) => void;
  selectedProblemTypes: ProblemType[];
}
export function ModeSelector({
  onStart,
  selectedProblemTypes
}: ModeSelectorProps) {
  const [mode, setMode] = useState<GameMode>('set_count');
  const [inputType, setInputType] = useState<InputType>('multiple_choice');
  const [count, setCount] = useState(10);
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advanced, setAdvanced] = useState<AdvancedOptions>(DEFAULT_ADVANCED_OPTIONS);
  const handleStart = () => {
    onStart({
      mode,
      inputType,
      problemCount: mode === 'set_count' ? count : undefined,
      advancedOptions: {
        ...advanced,
        difficulty
      }
    });
  };
  const updateAdvanced = (key: keyof AdvancedOptions, value: any) => {
    setAdvanced(prev => ({
      ...prev,
      [key]: value
    }));
  };
  return <div className="w-full max-w-2xl mx-auto p-4 space-y-8 pb-20" data-id="element-79">
      <Card className="p-6 md:p-8 space-y-8" data-id="element-80">
        {/* Game Mode Selection */}
        <div className="space-y-4" data-id="element-81">
          <label className="text-sm font-semibold text-slate-500 uppercase tracking-wider" data-id="element-82">
            Game Mode
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-83">
            <button onClick={() => setMode('set_count')} className={`p-4 rounded-xl border-2 text-left transition-all ${mode === 'set_count' ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-84">
              <div className="flex items-center gap-3 mb-2" data-id="element-85">
                <List className={`w-6 h-6 ${mode === 'set_count' ? 'text-indigo-600' : 'text-slate-400'}`} data-id="element-86" />
                <div className="font-semibold text-slate-900" data-id="element-87">Speed Run</div>
              </div>
              <div className="text-xs text-slate-500" data-id="element-88">
                Complete a set number of problems fast
              </div>
            </button>

            <button onClick={() => setMode('infinite')} className={`p-4 rounded-xl border-2 text-left transition-all ${mode === 'infinite' ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-89">
              <div className="flex items-center gap-3 mb-2" data-id="element-90">
                <Infinity className={`w-6 h-6 ${mode === 'infinite' ? 'text-indigo-600' : 'text-slate-400'}`} data-id="element-91" />
                <div className="font-semibold text-slate-900" data-id="element-92">Infinite</div>
              </div>
              <div className="text-xs text-slate-500" data-id="element-93">
                Keep going to build your streak
              </div>
            </button>

            <button onClick={() => setMode('no_errors')} className={`p-4 rounded-xl border-2 text-left transition-all ${mode === 'no_errors' ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-94">
              <div className="flex items-center gap-3 mb-2" data-id="element-95">
                <AlertTriangle className={`w-6 h-6 ${mode === 'no_errors' ? 'text-indigo-600' : 'text-slate-400'}`} data-id="element-96" />
                <div className="font-semibold text-slate-900" data-id="element-97">Error Free</div>
              </div>
              <div className="text-xs text-slate-500" data-id="element-98">
                One mistake and the game is over
              </div>
            </button>
          </div>
        </div>

        {/* Set Count Options */}
        {mode === 'set_count' && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} className="space-y-4" data-id="element-99">
            <label className="text-sm font-semibold text-slate-500 uppercase tracking-wider" data-id="element-100">
              Problem Count
            </label>
            <div className="flex gap-4" data-id="element-101">
              {[10, 25, 50].map(num => <button key={num} onClick={() => setCount(num)} className={`flex-1 py-3 rounded-lg font-medium border-2 transition-all ${count === num ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`} data-id="element-102">
                  {num}
                </button>)}
            </div>
          </motion.div>}

        {/* Difficulty Level - Now outside More Options */}
        <div className="space-y-4" data-id="element-103">
          <label className="text-sm font-semibold text-slate-500 uppercase tracking-wider" data-id="element-104">
            Difficulty Level
          </label>
          <div className="grid grid-cols-3 gap-3" data-id="element-105">
            {[{
            id: 'easy',
            label: 'Easy',
            desc: 'Smaller numbers'
          }, {
            id: 'medium',
            label: 'Medium',
            desc: 'Balanced'
          }, {
            id: 'hard',
            label: 'Hard',
            desc: 'Larger numbers'
          }].map(diff => <button key={diff.id} onClick={() => setDifficulty(diff.id as any)} className={`p-3 rounded-lg border-2 text-center transition-all ${difficulty === diff.id ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`} data-id="element-106">
                <div className="font-semibold text-sm" data-id="element-107">{diff.label}</div>
                <div className="text-xs opacity-75 mt-1" data-id="element-108">{diff.desc}</div>
              </button>)}
          </div>
        </div>

        {/* Advanced Options Toggle */}
        <div className="pt-4 border-t border-slate-100" data-id="element-109">
          <button onClick={() => setShowAdvanced(!showAdvanced)} className="flex items-center justify-center w-full py-2 text-slate-500 hover:text-indigo-600 transition-colors font-medium" data-id="element-110">
            <Settings className="w-4 h-4 mr-2" data-id="element-111" />
            More Options
            {showAdvanced ? <ChevronUp className="w-4 h-4 ml-2" data-id="element-112" /> : <ChevronDown className="w-4 h-4 ml-2" data-id="element-113" />}
          </button>

          <AnimatePresence data-id="element-114">
            {showAdvanced && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} className="overflow-hidden" data-id="element-115">
                <div className="pt-6 space-y-6" data-id="element-116">
                  {/* Input Style - Moved into More Options */}
                  <div className="space-y-3" data-id="element-117">
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-118">
                      Input Style
                    </label>
                    <div className="grid grid-cols-2 gap-3" data-id="element-119">
                      <button onClick={() => setInputType('multiple_choice')} className={`p-3 rounded-lg border-2 flex items-center justify-center gap-2 transition-all ${inputType === 'multiple_choice' ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`} data-id="element-120">
                        <CheckSquare className="w-4 h-4" data-id="element-121" />
                        <span className="font-medium text-sm" data-id="element-122">
                          Multiple Choice
                        </span>
                      </button>

                      <button onClick={() => setInputType('fill_in_blank')} className={`p-3 rounded-lg border-2 flex items-center justify-center gap-2 transition-all ${inputType === 'fill_in_blank' ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`} data-id="element-123">
                        <Keyboard className="w-4 h-4" data-id="element-124" />
                        <span className="font-medium text-sm" data-id="element-125">
                          Fill in Blank
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Answer Choice Count - Only show for Multiple Choice */}
                  {inputType === 'multiple_choice' && <div className="space-y-3" data-id="element-126">
                      <label className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-127">
                        Answer Choices
                      </label>
                      <div className="grid grid-cols-2 gap-3" data-id="element-128">
                        <button onClick={() => updateAdvanced('answerChoiceCount', 4)} className={`py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${advanced.answerChoiceCount === 4 ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`} data-id="element-129">
                          4 Choices
                        </button>
                        <button onClick={() => updateAdvanced('answerChoiceCount', 6)} className={`py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${advanced.answerChoiceCount === 6 ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`} data-id="element-130">
                          6 Choices
                        </button>
                      </div>
                    </div>}

                  <div className="space-y-3" data-id="element-131">
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-132">
                      Problem Display
                    </label>
                    <div className="grid grid-cols-2 gap-3" data-id="element-133">
                      {['horizontal', 'vertical'].map(opt => <button key={opt} onClick={() => updateAdvanced('orientation', opt)} className={`py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${advanced.orientation === opt ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`} data-id="element-134">
                          {opt.charAt(0).toUpperCase() + opt.slice(1)}
                        </button>)}
                    </div>
                  </div>

                  {/* 2. Number Types */}
                  <div className="space-y-3" data-id="element-135">
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-136">
                      Number Types
                    </label>
                    <div className="grid grid-cols-3 gap-3" data-id="element-137">
                      {[{
                    id: 'positive',
                    label: 'Positive'
                  }, {
                    id: 'negative',
                    label: 'Negative'
                  }, {
                    id: 'both',
                    label: 'Both'
                  }].map(opt => <button key={opt.id} onClick={() => updateAdvanced('numberType', opt.id)} className={`py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${advanced.numberType === opt.id ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`} data-id="element-138">
                          {opt.label}
                        </button>)}
                    </div>

                    {/* Second row - Value Type (only show for basic operations with horizontal orientation) */}
                    {advanced.orientation === 'horizontal' && selectedProblemTypes.some(type => ['addition', 'subtraction', 'multiplication', 'division'].includes(type)) && <div className="grid grid-cols-3 gap-3" data-id="element-139">
                          {[{
                    id: 'integers',
                    label: 'Integers'
                  }, {
                    id: 'fractions',
                    label: 'Fractions'
                  }, {
                    id: 'both',
                    label: 'Both'
                  }].map(opt => <button key={opt.id} onClick={() => updateAdvanced('valueType', opt.id)} className={`py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${advanced.valueType === opt.id ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`} data-id="element-140">
                              {opt.label}
                            </button>)}
                        </div>}
                  </div>

                  <div className="space-y-3" data-id="element-141">
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-142">
                      Number Format
                    </label>
                    <div className="grid grid-cols-2 gap-3" data-id="element-143">
                      {[{
                    id: 'whole',
                    label: 'Whole Numbers'
                  }, {
                    id: 'decimal',
                    label: 'Decimals'
                  }].map(opt => <button key={opt.id} onClick={() => updateAdvanced('numberFormat', opt.id)} className={`py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${advanced.numberFormat === opt.id ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`} data-id="element-144">
                          {opt.label}
                        </button>)}
                    </div>
                  </div>

                  <div className="space-y-3" data-id="element-145">
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-146">
                      Number Size (Digits)
                    </label>
                    <div className="grid grid-cols-4 gap-2" data-id="element-147">
                      {[1, 2, 3, 4].map(digits => <button key={digits} onClick={() => updateAdvanced('digitCount', digits)} className={`py-2 rounded-lg border-2 text-sm font-medium transition-all ${advanced.digitCount === digits ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`} data-id="element-148">
                          {digits === 4 ? '4+' : digits}
                        </button>)}
                    </div>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>

        <Button size="lg" fullWidth onClick={handleStart} className="mt-8" data-id="element-149">
          Start Game <Play className="w-5 h-5 ml-2" data-id="element-150" />
        </Button>
      </Card>
    </div>;
}